import { type FC, useCallback, useEffect, useRef, useState } from "react";

import { X } from "lucide-react";
import { IconButton } from "@/shared/components/ui/icon-button";
import { Dialog, DialogContent, DialogTitle } from "@/shared/components/ui/dialog";
import { useUser } from "@/shared/hooks/useAppContext";
import { useEstateSizeAnswers } from "@/shared/hooks/useEstateSizeAnswers";
import { useWillStepAnswers } from "@/shared/hooks/useWillStepAnswers";
import {
  useWillExtraction,
  getExecutorsFromExtraction,
  checkIsUserExecutor,
} from "@/shared/hooks/useWillExtraction";
import { toTitleCase } from "@/shared/components/document-viewer/insightUtils";

import { LegalAuthorityAmountStep } from "./LegalAuthorityAmountStep";
import { LegalAuthorityIntroStep } from "./LegalAuthorityIntroStep";
import { LegalAuthorityNotSureStep } from "./LegalAuthorityNotSureStep";

import { LegalAuthorityAttorneyQuestionStep } from "./LegalAuthorityAttorneyQuestionStep";
import { LegalAuthorityExecutorInviteStep } from "./LegalAuthorityExecutorInviteStep";
import { LegalAuthorityWillConfirmStep } from "./LegalAuthorityWillConfirmStep";
import { LegalAuthorityAttorneyInviteStep } from "./LegalAuthorityAttorneyInviteStep";
import { LegalAuthorityNoAttorneyStep } from "./LegalAuthorityNoAttorneyStep";
import { LegalAuthorityNoWillStep } from "./LegalAuthorityNoWillStep";
import { LegalAuthorityNotSureWillStep } from "./LegalAuthorityNotSureWillStep";
import { LegalAuthorityPropertyStep } from "./LegalAuthorityPropertyStep";
import { LegalAuthoritySidebar } from "./LegalAuthoritySidebar";
import { LegalAuthorityWillProcessingStep } from "./LegalAuthorityWillProcessingStep";
import { LegalAuthorityWillQuestionStep } from "./LegalAuthorityWillQuestionStep";
import { LegalAuthorityWillUploadStep } from "./LegalAuthorityWillUploadStep";
import { TriageModalQrStep } from "./TriageModalQrStep";
import { TriageModalRequestStep } from "./TriageModalRequestStep";

type LegalAuthorityStep =
  | "introduction"
  | "will-question"
  | "will-upload"
  | "will-upload-qr"
  | "will-upload-request"
  | "will-processing"
  | "will-confirm"
  | "will-executor-invite"
  | "will-attorney-question"
  | "will-attorney-invite"
  | "will-no-attorney"
  | "will-no-will"
  | "will-not-sure"
  | "estate-size"
  | "estate-amount"
  | "estate-not-sure";

interface LegalAuthorityModalProps {
  open: boolean;
  personFirstName: string;
  estateId: number | undefined;
  onComplete: () => void;
  onClose: () => void;
}

export const LegalAuthorityModal: FC<LegalAuthorityModalProps> = ({
  open,
  personFirstName,
  estateId,
  onComplete,
  onClose,
}) => {
  const user = useUser();
  const dialogRef = useRef<HTMLDivElement>(null);
  const {
    savedAnswers,
    isFetched: answersFetched,
    saveAnswers,
    completeTask: completeWillTask,
  } = useWillStepAnswers(estateId);
  const {
    savedAnswers: estateSizeAnswers,
    saveAnswers: saveEstateSize,
    completeTask: completeEstateSize,
  } = useEstateSizeAnswers(estateId);
  const { data: willExtraction } = useWillExtraction(open ? estateId : undefined);
  const [step, setStepRaw] = useState<LegalAuthorityStep>("introduction");
  const [willRequestedFrom, setWillRequestedFrom] = useState<string | null>(null);
  const previousStepRef = useRef<LegalAuthorityStep>("introduction");
  const [estateSizeBack, setEstateSizeBack] = useState<LegalAuthorityStep>("will-question");

  const userName = user?.full_name || `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();
  const { primary: primaryExecutor } = getExecutorsFromExtraction(willExtraction?.specific ?? null);
  const willExecutorName = primaryExecutor?.name ? toTitleCase(primaryExecutor.name) : "";
  const isUserExecutor = checkIsUserExecutor(willExecutorName, userName);
  const personName = personFirstName || "your loved one";
  const willSidebar = <LegalAuthoritySidebar activeStep={2} />;
  const hasRestored = useRef(false);

  useEffect(() => {
    if (!answersFetched || hasRestored.current) return;
    hasRestored.current = true;
    const saved = savedAnswers?.["resume-step"] as string | undefined;
    if (!saved || saved === "introduction") return;

    const target = saved === "will-processing" ? "will-upload" : (saved as LegalAuthorityStep);
    const backTarget = savedAnswers?.["estate-size-back"] as LegalAuthorityStep | undefined;
    if (backTarget) setEstateSizeBack(backTarget);
    const savedRequestedFrom = savedAnswers?.["will-requested-from"] as string | undefined;
    if (savedRequestedFrom) setWillRequestedFrom(savedRequestedFrom);
    setStepRaw((current) => {
      previousStepRef.current = current;
      return target;
    });
  }, [answersFetched, savedAnswers]);

  const setStep = useCallback(
    (next: LegalAuthorityStep) => {
      setStepRaw((current) => {
        previousStepRef.current = current;
        return next;
      });
      if (estateId) {
        const partial: Record<string, string> = { "resume-step": next };
        if (
          next === "estate-size" &&
          previousStepRef.current !== "estate-amount" &&
          previousStepRef.current !== "estate-not-sure"
        ) {
          setEstateSizeBack(previousStepRef.current);
          partial["estate-size-back"] = previousStepRef.current;
        }
        saveAnswers(partial).catch(console.error);
      }
    },
    [estateId, saveAnswers],
  );

  return (
    <Dialog open={open}>
      <DialogContent
        size="flow"
        className="!p-0 !gap-0 overflow-hidden"
        ref={dialogRef}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Path to legal authority</DialogTitle>

        <IconButton
          onClick={onClose}
          className="absolute right-4 top-4 z-10"
          label="Close"
          tooltip={false}
        >
          <X />
        </IconButton>

        {step === "introduction" && (
          <LegalAuthorityIntroStep
            personFirstName={personFirstName}
            onContinue={() => setStep("will-question")}
          />
        )}

        {step === "will-question" && estateId ? (
          <LegalAuthorityWillQuestionStep
            personFirstName={personFirstName}
            estateId={estateId}
            onBack={() => setStep("introduction")}
            onContinue={(answer) => {
              if (answer === "yes") setStep("will-upload");
              else if (answer === "no") setStep("will-no-will");
              else setStep("will-not-sure");
            }}
          />
        ) : null}

        {step === "will-upload" && (
          <LegalAuthorityWillUploadStep
            personFirstName={personFirstName}
            onBack={() => setStep(willRequestedFrom ? "will-upload-request" : "will-question")}
            onContinue={() => setStep("will-processing")}
            onPhoneUpload={() => setStep("will-upload-qr")}
            onRequestUpload={() => setStep("will-upload-request")}
            requestedFrom={willRequestedFrom}
          />
        )}

        {step === "will-upload-qr" && (
          <TriageModalQrStep
            title={`Add ${personName}'s will`}
            description={`The will decides much of which court process ${personName}'s estate needs. Share it once, and it's saved for anywhere else that asks.`}
            sidebar={willSidebar}
            documentType="will"
            onBack={() => setStep("will-upload")}
            onFileUploaded={() => setStep("will-processing")}
          />
        )}

        {step === "will-upload-request" && (
          <TriageModalRequestStep
            title={`Add ${personName}'s will`}
            description={`The will decides much of which court process ${personName}'s estate needs. Share it once, and it's saved for anywhere else that asks.`}
            sidebar={willSidebar}
            documentType="will"
            documentLabel="Will"
            personFirstName={personFirstName}
            onBack={() => setStep("will-upload")}
            onRequestSent={(name) => {
              setWillRequestedFrom(name);
              if (estateId) {
                saveAnswers({ "will-requested-from": name }).catch(console.error);
              }
              setStep("will-upload");
            }}
          />
        )}

        {step === "will-processing" && estateId ? (
          <LegalAuthorityWillProcessingStep
            personFirstName={personFirstName}
            estateId={estateId}
            onComplete={() => setStep("will-confirm")}
          />
        ) : null}

        {step === "will-confirm" && estateId ? (
          <LegalAuthorityWillConfirmStep
            estateId={estateId}
            onBack={() => setStep("will-upload")}
            onContinue={() => {
              if (!isUserExecutor && willExecutorName) {
                setStep("will-executor-invite");
              } else {
                setStep("will-attorney-question");
              }
            }}
          />
        ) : null}

        {step === "will-executor-invite" && estateId ? (
          <LegalAuthorityExecutorInviteStep
            personFirstName={personFirstName}
            estateId={estateId}
            executorName={willExecutorName}
            onBack={() => setStep("will-confirm")}
            onContinue={() => setStep("will-attorney-question")}
            onSkip={() => setStep("will-attorney-question")}
          />
        ) : null}

        {step === "will-attorney-question" && estateId ? (
          <LegalAuthorityAttorneyQuestionStep
            estateId={estateId}
            onBack={() => {
              if (!isUserExecutor && willExecutorName) {
                setStep("will-executor-invite");
              } else {
                setStep("will-confirm");
              }
            }}
            onContinue={(answer) => {
              if (answer === "none") setStep("will-no-attorney");
              else setStep("will-attorney-invite");
            }}
          />
        ) : null}

        {step === "will-attorney-invite" && estateId ? (
          <LegalAuthorityAttorneyInviteStep
            personFirstName={personFirstName}
            estateId={estateId}
            onBack={() => setStep("will-attorney-question")}
            onContinue={() => setStep("estate-size")}
          />
        ) : null}

        {step === "will-no-attorney" && (
          <LegalAuthorityNoAttorneyStep
            onBack={() => setStep("will-attorney-question")}
            onContinue={() => setStep("estate-size")}
          />
        )}

        {step === "will-no-will" && (
          <LegalAuthorityNoWillStep
            onBack={() => setStep("will-question")}
            onContinue={() => setStep("estate-size")}
          />
        )}

        {step === "will-not-sure" && (
          <LegalAuthorityNotSureWillStep
            onBack={() => setStep("will-question")}
            onContinue={() => setStep("estate-size")}
          />
        )}

        {step === "estate-size" && estateId && (
          <LegalAuthorityPropertyStep
            personFirstName={personFirstName}
            estateId={estateId}
            willDocumentId={willExtraction?.documentId}
            savedOwnerships={estateSizeAnswers?.["property-ownerships"]}
            portalContainer={dialogRef.current}
            onBack={() => setStep(estateSizeBack)}
            onContinue={(propertyOwnerships) => {
              saveEstateSize({ "property-ownerships": propertyOwnerships }).catch(console.error);
              setStep("estate-amount");
            }}
          />
        )}

        {step === "estate-amount" && (
          <LegalAuthorityAmountStep
            personFirstName={personFirstName}
            onBack={() => setStep("estate-size")}
            onContinue={async (answer, threshold, amountState) => {
              try {
                await saveEstateSize({
                  "estate-amount": answer,
                  "estate-threshold": threshold,
                  "estate-threshold-state": amountState,
                });
                await Promise.all([completeWillTask(), completeEstateSize()]);
                onComplete();
              } catch (err) {
                console.error(err);
              }
            }}
          />
        )}

        {step === "estate-not-sure" && (
          <LegalAuthorityNotSureStep
            personFirstName={personFirstName}
            onBack={() => setStep("estate-amount")}
            onContinue={async () => {
              try {
                await Promise.all([completeWillTask(), completeEstateSize()]);
                onComplete();
              } catch (err) {
                console.error(err);
              }
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
